import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { usePaymentParams } from './PaymentRouter';

/**
 * FastTrackPage
 *
 * Auto-creates session using all provided params and access token
 * Similar to Fonbnk's /auto-order page
 * Skips all screens and goes directly to payment instructions
 */
export function FastTrackPage() {
  const navigate = useNavigate();
  const params = usePaymentParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    // Only run if params are available
    if (params) {
      createFastTrackSession();
    }
  }, [params]);

  async function createFastTrackSession() {
    // Validate all required params are present
    if (!params?.at) {
      setError('Authentication token required for fast-track');
      setLoading(false);
      return;
    }

    if (!params.amount || !params.walletAddress || !params.type || !params.network || !params.asset) {
      setError('Missing required parameters for fast-track');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.pouchfinance.xyz/v2/fast-track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${params.at}`
        },
        body: JSON.stringify({
          source: params.source,
          signature: params.signature,
          type: params.type,
          network: params.network,
          asset: params.asset,
          amount: params.amount,
          currency: params.currency || 'local',
          countryCode: params.countryCode,
          walletAddress: params.walletAddress,
          at: params.at,
          quoteId: params.quoteId
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create fast-track session');
      }

      const data = await response.json();

      setSessionId(data.sessionId);

      // Redirect to instructions page after 2 seconds
      setTimeout(() => {
        navigate(`/pay/instructions?sessionId=${data.sessionId}`);
      }, 2000);

    } catch (err: any) {
      console.error('Fast-track error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Fast-Track Failed
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/pay')}
            className="px-6 py-2 bg-brand text-white rounded-lg hover:opacity-90 font-medium"
          >
            Start Regular Flow
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
        {loading ? (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-brand mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Creating Your Order
            </h1>
            <p className="text-gray-600">
              Please wait while we set up your payment...
            </p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Order Created!
            </h1>
            <p className="text-gray-600 mb-4">
              Redirecting to payment instructions...
            </p>
            {sessionId && (
              <p className="text-sm text-gray-500 font-mono">
                Session: {sessionId.slice(0, 8)}...
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
